import React from 'react';
import { cn } from '@/lib/utils';

/**
 * Renders a coach's image, or a styled initials placeholder when no
 * image_url is set. Eliminates the "empty string was passed to %s attribute"
 * React warning that occurs when a raw <img src={coach.image_url}> gets an
 * empty image_url and triggers an extra network fetch for the current page.
 *
 * Drop-in replacement for raw <img> tags used to render coach.image_url.
 *
 * Usage:
 *   <CoachImage src={coach.image_url} alt={coach.name}
 *               className="w-full h-56 object-cover rounded-lg" />
 */
export const CoachImage = ({ src, alt, name, className, fallbackClassName }) => {
  const hasImage = typeof src === 'string' && src.trim().length > 0;

  if (hasImage) {
    return (
      <img
        src={src}
        alt={alt || name || 'Coach'}
        className={className}
        loading="lazy"
      />
    );
  }

  // Initials placeholder — Sister Diamonds palette, never crashes
  const initials = (name || alt || 'C')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s.replace(/[^A-Za-z]/g, '').charAt(0).toUpperCase())
    .join('') || 'C';

  return (
    <div
      role="img"
      aria-label={alt || name || 'Coach'}
      className={cn(
        'bg-gradient-to-br from-sand to-forest/15 text-forest flex items-center justify-center font-heading font-semibold select-none',
        className,
        fallbackClassName
      )}
    >
      <span className="text-2xl">{initials}</span>
    </div>
  );
};

export default CoachImage;
